/**
 * CalendarView.jsx — TM 월별 달력 컴포넌트
 *
 * 한 달의 콜 데이터를 달력 형태로 시각화합니다.
 * - 날짜 셀에 콜 건수 배지(call_date 기준) + 계약 배지(contract_date 기준) 표시
 * - 계약 배지는 contract_date에 배치 (콜일자가 아닌 실제 계약일)
 * - 계약 배지 클릭 시 하단 상세 패널에 상호·상품·장수 정보 표시
 * - 신규 계약: 초록(#10b981), 재주문: 황색(#f59e0b) — is_reorder 필드 기반
 * - 모바일: 일/토 열 축소 (0.5fr)
 * dark mode: CSS 변수 토큰 사용
 *
 * Props:
 *   year    {number}   — 표시할 연도
 *   month   {number}   — 표시할 월 (1~12)
 *   calls   {object[]} — 해당 월의 콜 데이터 배열 (call_date 기준 — 콜 건수 집계용)
 *   loading {boolean}  — 데이터 로딩 중 여부
 */
import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { todayKST, dayOfWeekUTC } from '../../../utils/date';
import { useIsMobile, lastDayOf } from './utils';

// 요일 헤더 (일요일 시작 — 한국 달력 표준)
const CAL_DOW = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * contract_items 배열 → [{name, count}] 달력 표시용 배열로 변환
 * isService=false 항목만 포함
 */
function parseContractItems(contractItems) {
  return (contractItems || [])
    .filter((i) => !i.isService)
    .map((i) => ({ name: i.ticketTypeName || '?', count: Number(i.quantity) || null }));
}

function compactTicketName(name) {
  const text = String(name || '');
  const match = text.match(/\(([^)]+)\)/);
  return match?.[1] || text;
}

function defaultBuildCalendarContractsPath({ year, month }) {
  const params = new URLSearchParams({ year, month });
  return `/api/tm/calendar-contracts?${params.toString()}`;
}

export default function CalendarView({
  year,
  month,
  calls,
  loading,
  buildCalendarContractsPath = defaultBuildCalendarContractsPath,
}) {
  const isMobile = useIsMobile();
  // 클릭한 계약 상세를 보여줄 선택 상태
  const [selected, setSelected] = useState(null); // { id, is_new, company_name, region, items }

  // ── 달력 전용 계약 데이터 (contract_date 기준 조회) ───────────────────────
  // 부모(TmSummary)의 calls는 call_date 기준 → 콜 건수 배지용
  // calContracts는 contract_date 기준 → 계약 배지를 계약일에 배치하기 위함
  const [calContracts, setCalContracts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(buildCalendarContractsPath({ year, month }));
        if (!cancelled && res.data?.success) setCalContracts(res.data.data || []);
      } catch {
        /* 달력 계약 조회 실패 — 콜 건수는 정상 표시, 계약 배지만 미표시 */
      }
    })();
    return () => { cancelled = true; };
  }, [buildCalendarContractsPath, year, month]);

  // ── 날짜별 집계: 콜 건수(call_date) + 계약 배지(contract_date) ──────────
  const dailyStats = useMemo(() => {
    const map = {};

    // 1) 콜 건수: call_date 기준 (모든 콜 — 계약 여부 무관)
    (calls || []).forEach((row) => {
      const d = row.call_date;
      if (!d) return;
      if (!map[d]) map[d] = { callCount: 0, contracts: [] };
      map[d].callCount += 1;
    });

    // 2) 계약 배지: contract_date 기준 배치, is_reorder로 신규/재주문 구분
    (calContracts || []).forEach((con) => {
      const d = con.contract_date;
      if (!d) return;
      if (!map[d]) map[d] = { callCount: 0, contracts: [] };
      const items = parseContractItems(con.contract_items);
      map[d].contracts.push({
        id:                con.id,
        contract_id:       con.contract_id,
        event_type:        con.event_type || 'contract',
        contract_status:   con.contract_status || 'normal',
        incentive_include_cancelled: Boolean(con.incentive_include_cancelled),
        incentive_decision_status: con.incentive_decision_status || 'pending',
        is_new:            !con.is_reorder, // is_reorder=false → 신규, is_reorder=true → 재주문
        company_name:      con.company_name || '-',
        region:            con.region || '-',
        sales_person_name: con.sales_person_name || '',
        items,
        // contract_items 없을 때 배지/상세 패널 fallback용
        ticket_type:  con.ticket_type || null,
      });
    });

    return map;
  }, [calls, calContracts]);

  // dayOfWeekUTC, todayKST: utils/date.js 사용 (new Date 직접 사용 금지)
  const firstWeekday = dayOfWeekUTC(`${year}-${String(month).padStart(2, '0')}-01`);
  const offset       = firstWeekday;
  const daysInMonth  = lastDayOf(year, month);
  const todayStr     = todayKST();

  const cells = [
    ...Array(offset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  // 모바일: 일/토 열을 0.5fr로 좁혀 평일 배지 공간 확보
  const calGridCols = isMobile
    ? '0.5fr 1fr 1fr 1fr 1fr 1fr 0.5fr'
    : 'repeat(7, 1fr)';

  return (
    <div>
      {/* ── 요일 헤더 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: calGridCols, gap: isMobile ? 2 : 4, marginBottom: 2 }}>
        {CAL_DOW.map((dow, i) => (
          <div key={dow} style={{
            textAlign: 'center', fontSize: isMobile ? 10 : 11, fontWeight: 700,
            // 일요일(0) → 빨강, 토요일(6) → 파랑, 평일 → 서브 텍스트
            color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : 'var(--text-muted)',
            padding: isMobile ? '2px 0' : '4px 0', letterSpacing: '0.03em',
          }}>
            {dow}
          </div>
        ))}
      </div>

      {/* ── 날짜 셀 그리드 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: calGridCols, gap: isMobile ? 2 : 4 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} style={{ minHeight: isMobile ? 42 : 54 }} />;

          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const stats   = dailyStats[dateStr] || { callCount: 0, contracts: [] };
          const isToday = dateStr === todayStr;
          const colIdx  = i % 7;
          const isSun   = colIdx === 0;
          const isSat   = colIdx === 6;

          return (
            <div key={dateStr} style={{
              minHeight: isMobile ? 42 : 54,
              minWidth: 0,
              overflow: 'hidden',
              padding: isMobile ? '3px 3px' : '5px 6px',
              borderRadius: isMobile ? 5 : 8,
              // 오늘: 인디고 강조 테두리, 아니면 일반 테두리
              border: isToday ? '1.5px solid #6366f1' : '1px solid var(--border)',
              background: isToday ? 'rgba(99,102,241,0.08)' : 'var(--bg-card)',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}>
              {/* 날짜 숫자 */}
              <div style={{
                fontSize: isMobile ? 11 : 12, fontWeight: isToday ? 700 : 400,
                color: isToday ? '#6366f1' : isSat ? '#3b82f6' : isSun ? '#ef4444' : 'var(--text-sub)',
                lineHeight: 1,
              }}>
                {day}
              </div>

              {/* 콜 건수 배지 */}
              {stats.callCount > 0 && (
                <div style={{
                  fontSize: isMobile ? 9 : 10, fontWeight: 500, color: 'var(--text-muted)',
                  background: 'var(--bg-subtle)', borderRadius: 3,
                  padding: isMobile ? '1px 3px' : '1px 5px',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {isMobile ? stats.callCount : `콜 ${stats.callCount}`}
                </div>
              )}

              {/* 계약 배지 목록 */}
              {stats.contracts.map((contract) => {
                // "{상품} {장수} ({마케터})" 형식으로 배지 라벨 생성
                const isCancelEvent = contract.event_type === 'cancel';
                const isIncludedCancel = contract.incentive_decision_status === 'included'
                  || contract.incentive_include_cancelled;
                const cancelLabel = isIncludedCancel ? '취소 · 인센포함' : '취소';
                const badgeColor = isCancelEvent ? (isIncludedCancel ? '#2563eb' : '#64748b') : contract.is_new ? '#10b981' : '#f59e0b';
                const spSuffix = contract.sales_person_name
                  ? ` (${contract.sales_person_name})`
                  : '';
                const itemLabel = contract.items.length > 0
                  ? contract.items.map((it) => {
                      const name = isMobile ? compactTicketName(it.name) : it.name;
                      return it.count != null ? `${name} ${it.count}` : name;
                    }
                    ).join(', ') + spSuffix
                  : contract.ticket_type
                    ? (isMobile ? compactTicketName(contract.ticket_type) : contract.ticket_type) + spSuffix
                    : '계약' + spSuffix;

                return (
                  <button
                    key={contract.id}
                    onClick={() => setSelected((prev) => prev?.id === contract.id ? null : contract)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      fontSize: isMobile ? 8 : 10, fontWeight: 600,
                      color: '#fff',
                      background: badgeColor,
                      border: selected?.id === contract.id
                        ? '2px solid rgba(15,23,42,0.9)'
                        : '2px solid transparent',
                      borderRadius: 4,
                      padding: isMobile ? '1px 3px' : '2px 5px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    {isCancelEvent ? `${cancelLabel} · ${itemLabel}` : itemLabel}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── 계약 상세 패널 (클릭 시 표시) ── */}
      {selected && (
        <div style={{
          marginTop: 12, padding: '12px 16px',
          background: 'var(--bg-subtle)', border: '1px solid var(--border)',
          borderRadius: 8, display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', gap: 16,
        }}>
          {/* 신규/재주문 칩 */}
          <span style={{
            padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700,
            color: '#fff', background: selected.event_type === 'cancel'
              ? ((selected.incentive_decision_status === 'included' || selected.incentive_include_cancelled) ? '#2563eb' : '#64748b')
              : selected.is_new ? '#10b981' : '#f59e0b',
            whiteSpace: 'nowrap',
          }}>
            {selected.event_type === 'cancel'
              ? ((selected.incentive_decision_status === 'included' || selected.incentive_include_cancelled) ? '취소 · 인센포함' : '취소')
              : selected.is_new ? '신규' : '재주문'}
            <span style={{ display: 'none' }}>
            {selected.is_new ? '신규' : '재주문'}
          </span>

          {/* 상호 */}
          </span>

          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            {selected.company_name}
          </span>

          {/* 지역 */}
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{selected.region}</span>

          {/* 마케터 */}
          {selected.sales_person_name && (
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>
              {selected.sales_person_name}
            </span>
          )}

          {/* 상품 목록 + 장수 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {selected.items.length > 0
              ? selected.items.map((item, ii) => (
                  <span key={ii} style={{
                    fontSize: 12, color: 'var(--text-sub)',
                    background: 'var(--border)', borderRadius: 6,
                    padding: '2px 8px', whiteSpace: 'nowrap',
                  }}>
                    {item.name}{item.count != null ? ` · ${item.count}장` : ''}
                  </span>
                ))
              : selected.ticket_type
                ? (
                  <span style={{
                    fontSize: 12, color: 'var(--text-sub)',
                    background: 'var(--border)', borderRadius: 6,
                    padding: '2px 8px', whiteSpace: 'nowrap',
                  }}>
                    {selected.ticket_type}
                  </span>
                )
                : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>-</span>
            }
          </div>

          {/* 닫기 버튼 */}
          <button
            onClick={() => setSelected(null)}
            style={{
              marginLeft: 'auto', padding: '3px 10px', fontSize: 11,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 6, cursor: 'pointer', color: 'var(--text-muted)',
            }}
          >
            닫기
          </button>
        </div>
      )}

      {/* 로딩 안내 */}
      {loading && (
        <div style={{ textAlign: 'center', marginTop: 12, color: 'var(--text-muted)', fontSize: 12 }}>
          불러오는 중...
        </div>
      )}
    </div>
  );
}
